/** Orchestration controller for the History page.
 *
 * Layer: `controller` per docs/design/spa-account-extraction-260524.md §3.6
 * and tools/lint/lint-frontend-layer-imports.py SPA_HISTORY_LAYERS.
 *
 * Responsibilities:
 *   - Mutable page state (currentPage, totalJobs, allJobsLoaded, perPage).
 *   - Wire delegated DOM click handlers (`data-action="..."` pattern).
 *   - Call model layer for fetches + mutations; call view layer for HTML.
 *   - DOM writes: all `innerHTML`, `textContent`, `style.display` mutations
 *     live here (purity contract: view is string-only; DOM is controller).
 *
 * history.html stays UNCHANGED until AC.9 wires the module tag.
 *
 * Per CLAUDE.md rule #11a: class form because 4 mutable state vars are shared
 * across methods (currentPage, totalJobs, allJobsLoaded, perPage).
 *
 * Extracted from web/static/history.html (Phase AC.8). history.html is
 * unchanged until AC.9 wires the module tag.
 */
import { backendMirror, HISTORY_PER_PAGE, keyJobs, registerHistoryJobsPage, deleteJobMutation, clearHistoryMutation, deleteAllFilesMutation, } from './model.js';
import { renderJobTableHtml, renderJobRowsHtml } from './view.js';
import { getAccessToken, setHandoffToken } from '../spa-shared/auth-surface.js';
// ─── Controller ───────────────────────────────────────────────────────────
export class HistoryController {
    _esc;
    _authFetch;
    /** Mutable page state — CLAUDE.md rule #11a: class because ≥2 shared vars. */
    _currentPage = 1;
    _totalJobs = 0;
    _allJobsLoaded = [];
    _perPage = HISTORY_PER_PAGE;
    constructor(deps) {
        this._esc = deps.esc;
        this._authFetch = deps.authFetch;
    }
    // ─── Public API ────────────────────────────────────────────────────────
    /** Register the page-1 query and kick off the initial fetch.
     *  Called once during page bootstrap (AC.9). */
    async loadHistory() {
        registerHistoryJobsPage(this._authFetch, 1);
        try {
            const resp = await this._authFetch(`/api/account/jobs?per_page=${this._perPage}&page=1`);
            if (resp.status === 401) {
                this._showNotLoggedIn();
                return;
            }
            if (!resp.ok)
                throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json();
            this._renderHistoryContent(data);
        }
        catch (_e) {
            this._showLoadError();
        }
    }
    /** Load the next page and append rows.
     *  Called by the "Load more" button click handler. */
    async loadMoreJobs() {
        this._currentPage += 1;
        const btn = document.getElementById('load-more-btn'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        if (btn) {
            btn.disabled = true;
            btn.textContent = 'Loading...';
        }
        try {
            registerHistoryJobsPage(this._authFetch, this._currentPage);
            const resp = await this._authFetch(`/api/account/jobs?page=${this._currentPage}&per_page=${this._perPage}`);
            if (!resp.ok)
                throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json();
            const newJobs = data.jobs || [];
            this._allJobsLoaded = this._allJobsLoaded.concat(newJobs);
            this._appendJobRows(newJobs);
            if (this._allJobsLoaded.length >= this._totalJobs) {
                const row = document.getElementById('load-more-row'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
                if (row)
                    row.style.display = 'none';
            }
            else if (btn) {
                btn.disabled = false;
                btn.textContent = 'Load more';
            }
        }
        catch (_e) {
            if (btn) {
                btn.disabled = false;
                btn.textContent = 'Load more';
            }
            alert('Failed to load more jobs. Please try again.');
        }
    }
    /** Delete one job row. Optimistically removes the row via backendMirror. */
    async handleDeleteJob(jobId, rowEl) {
        if (!confirm('Delete this job and its files from storage?'))
            return;
        const btn = rowEl
            ? rowEl.querySelector('[data-action="delete-job"]')
            : null;
        if (btn)
            btn.disabled = true;
        try {
            await backendMirror.mutate(deleteJobMutation(this._authFetch), jobId);
            if (rowEl)
                rowEl.remove();
        }
        catch (_e) {
            alert('Failed to delete. Please try again.');
            if (btn)
                btn.disabled = false;
        }
    }
    /** Clear all completed/failed jobs after confirmation. */
    async handleClearHistory() {
        if (!confirm('Clear all completed and failed jobs from your history? Pending jobs will not be affected.'))
            return;
        try {
            await backendMirror.mutate(clearHistoryMutation(this._authFetch), undefined);
            location.reload();
        }
        catch (_e) {
            alert('Failed to clear history. Please try again.');
        }
    }
    /** Delete ALL user files after double confirmation. */
    async handleDeleteAllFiles() {
        if (!confirm('Delete ALL your files from storage? This cannot be undone.'))
            return;
        try {
            await backendMirror.mutate(deleteAllFilesMutation(this._authFetch), undefined);
            location.reload();
        }
        catch (_e) {
            alert('Failed to delete files. Please try again.');
        }
    }
    /** Wire all delegated click handlers onto the document.  // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
     *  Call once during bootstrap (AC.9). */
    attach() {
        document.addEventListener('click', (e) => {
            const target = e.target;
            if (!target)
                return;
            // ── delete-job ──────────────────────────────────────────────
            const deleteBtn = target.closest('[data-action="delete-job"]');
            if (deleteBtn) {
                e.preventDefault();
                const jobId = deleteBtn.dataset['jobId'] ?? '';
                const row = deleteBtn.closest('tr');
                void this.handleDeleteJob(jobId, row);
                return;
            }
            // ── clear-history ────────────────────────────────────────────
            const clearBtn = target.closest('[data-action="clear-history"]');
            if (clearBtn) {
                e.preventDefault();
                void this.handleClearHistory();
                return;
            }
            // ── delete-all-files ─────────────────────────────────────────
            const deleteAllBtn = target.closest('[data-action="delete-all-files"]');
            if (deleteAllBtn) {
                e.preventDefault();
                void this.handleDeleteAllFiles();
                return;
            }
            // ── load-more ────────────────────────────────────────────────
            const loadMoreBtn = target.closest('[data-action="load-more"]');
            if (loadMoreBtn) {
                e.preventDefault();
                void this.loadMoreJobs();
                return;
            }
            // ── manage-account-link: forward auth token (security audit F-6) ──
            // Uses the canonical shared auth surface (getAccessToken / setHandoffToken)
            // — previously read the RETIRED flat global `window.AdaSupabase`, which
            // was undefined post window-adatool-ns migration (4899f24b63), so the
            // handoff token was never forwarded (latent second effect of the same
            // dead-global bug). Epic cross-spa-auth-propagation-260705 Phase 2.
            const accountLink = target.closest('.manage-account-link');
            if (accountLink) {
                e.preventDefault();
                void getAccessToken().then(token => {
                    if (token)
                        setHandoffToken(token);
                    window.location.href = '/account';
                });
            }
        });
    }
    // ─── Private DOM-writing helpers ───────────────────────────────────────
    /** Render the full history view after the first page loads. */
    _renderHistoryContent(data) {
        const loadingEl = document.getElementById('loading-indicator'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        if (loadingEl)
            loadingEl.style.display = 'none';
        const contentEl = document.getElementById('history-content'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        if (contentEl)
            contentEl.style.display = '';
        this._allJobsLoaded = data.jobs || [];
        this._totalJobs = data.jobs_total ?? this._allJobsLoaded.length;
        this._currentPage = 1;
        const countLabel = document.getElementById('job-count-label'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        if (countLabel)
            countLabel.textContent = `(${this._totalJobs} total)`;
        const container = document.getElementById('job-history-container'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        if (container)
            container.innerHTML = renderJobTableHtml(this._esc, this._allJobsLoaded);
        if (this._totalJobs > this._allJobsLoaded.length) {
            const loadMoreRow = document.getElementById('load-more-row'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            if (loadMoreRow)
                loadMoreRow.style.display = '';
            // Load-more button wired via delegated click handler in attach().
        }
        if (this._totalJobs > 0) {
            const clearBtn = document.getElementById('clear-history-btn'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            if (clearBtn)
                clearBtn.style.display = '';
            const deleteBtn = document.getElementById('delete-all-btn'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            if (deleteBtn)
                deleteBtn.style.display = '';
        }
    }
    /** Append additional rows to the existing `<tbody id="job-tbody">`. */
    _appendJobRows(jobs) {
        const tbody = document.getElementById('job-tbody'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        if (tbody) {
            tbody.insertAdjacentHTML('beforeend', renderJobRowsHtml(this._esc, jobs));
        }
    }
    _showNotLoggedIn() {
        const loadingEl = document.getElementById('loading-indicator'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        if (loadingEl)
            loadingEl.style.display = 'none';
        const notLoggedIn = document.getElementById('not-logged-in'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        if (notLoggedIn)
            notLoggedIn.style.display = '';
    }
    _showLoadError() {
        const loadingEl = document.getElementById('loading-indicator'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        if (loadingEl)
            loadingEl.style.display = 'none';
        const banner = document.getElementById('error-banner'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        if (banner) {
            banner.textContent = 'Failed to load job history. Please try again.';
            banner.style.display = '';
        }
    }
    // ─── Accessors (exposed for tests) ────────────────────────────────────
    get currentPage() { return this._currentPage; }
    get totalJobs() { return this._totalJobs; }
    get allJobsLoaded() { return this._allJobsLoaded; }
    /** Invalidate all history query keys via backendMirror (for tests / post-action refresh). */
    invalidateAll() {
        backendMirror.invalidate(keyJobs(1));
    }
}
